import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultActionStatus, IActionStatus } from 'src/Definitions';
import { ActionStatusDto, Client, IActionStatusDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';

const ColumnSettingsDto = 'ActionStatusDto';

export default class ActionStatusesStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public actionStatuses: IActionStatus[];
    @observable
    public selectedActionStatus?: IActionStatusDto;
    public dataTableStore: DataTableStore<IActionStatusDto, IActionStatus>;

    constructor(private globalStore: GlobalStore, private client: Client) {
        this.dataTableStore = new DataTableStore<IActionStatusDto, IActionStatus>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllActionStatuses(filter, page, undefined, orderBy, includeInactives),
            (actionStatus) => this.setupActionStatuses(actionStatus),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedActionStatus = undefined;
    }

    @action.bound
    public disableActionStatus(actionStatus: IActionStatus) {
        this.isLoading = true;
        return this.client
            .archiveActionStatus(actionStatus.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndActionStatuses() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllActionStatuses(pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllActionStatuses(undefined, pageNumber, pageSize, 'SortOrder')
            .then((resp: SwaggerResponse<IActionStatusDto[]>) =>
                runInAction(() => {
                    this.actionStatuses = this.setupActionStatuses(resp.result);
                })
            );
    }

    @action.bound
    public openDialogWithActionStatus(actionStatus: IActionStatus) {
        this.isModalOpen = true;
        this.selectedActionStatus = actionStatus;
    }

    @action.bound
    public recoverActionStatus(actionStatus: IActionStatus) {
        this.isLoading = true;
        return this.client
            .reactivateActionStatus(actionStatus.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        this.dataTableStore.loadData();
        this.closeDialog();
    }

    @action.bound
    public saveActionStatus(actionStatus: IActionStatus) {
        this.isLoading = true;
        let promise;
        const actionStatusDto = new ActionStatusDto(actionStatus);
        if (actionStatus.isNew) {
            promise = this.client.createActionStatus(actionStatusDto);
        } else {
            promise = this.client.updateActionStatus(actionStatus.id, actionStatusDto);
        }
        return promise
            .then(() => {
                this.resetUIState(actionStatus.isNew);
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupActionStatuses = (actionStatuses: IActionStatusDto[]) => {
        return actionStatuses.map((x) => this.setupActionStatusMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (actionStatus: IActionStatusDto): IActionStatus => {
        return { ...DefaultActionStatus(), ...actionStatus };
    };

    private setupActionStatusMenuItems = (actionStatus: IActionStatus) => {
        actionStatus.menuItems = [];
        if (actionStatus.isActive) {
            actionStatus.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableActionStatus(actionStatus),
            });
        } else {
            actionStatus.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverActionStatus(actionStatus),
            });
        }
        return actionStatus;
    };
}



// WEBPACK FOOTER //
// ./src/stores/ActionStatusesStore/ActionStatusesStore.ts